import { AirportResult } from '@components/search/airports/Airports';
import React from 'react';
import { groupBy } from 'lodash';
import styled from '@emotion/styled';
import { ThemeProps } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { Airport } from '@model/iceberg/service/price-calendar';
import { NO_AIRPORT_CODE } from '@model/iceberg/airports';
import { Option } from './Option';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { AirportSorter } from '@components/search/airports/list/airport-sorter';
import { GroupOption } from '@components/search/airports/list/GroupOption';

export interface ListProps {
  values: Array<string>;
  available: Array<string>;
  input: string;
  options: Array<AirportResult>;
  onChange: (payload: Array<string>) => void;
  isNotGroupResults?: boolean;
  noFlightsLabel?: JSX.Element;
}

interface ContainerProps extends ThemeProps {
  displayInColumns?: boolean;
}
const Container: any = styled.div(({ theme, displayInColumns }: ContainerProps) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  padding: theme.custom.spacing.medium + theme.custom.spacing.small,
  overflowX: 'auto',
  paddingBottom: theme.custom.spacing.xLarge,
  ...(displayInColumns && {
    [mq.small]: {
      flexDirection: 'row',
      columnGap: 50,
      width: '100%',
      ['> div']: {
        width: '50%'
      }
    },
    [mq.large]: {
      width: 680
    }
  })
}));

const GroupTitle = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  marginBottom: theme.custom.spacing.medium
}));

const AirportsTitle = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  marginBottom: theme.custom.spacing.medium,
  marginTop: theme.custom.spacing.medium,
  [mq.small]: {
    marginTop: 0
  }
}));

const NoResults: any = styled.div(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.group11.base,
  ...(theme.custom.typography.paragraph as any)
}));

export const List = ({ values, available, options, onChange, isNotGroupResults, noFlightsLabel }: ListProps) => {
  const t: any = useI18NextContext();
  const handleOnSelect = (code: string) => {
    if (code === NO_AIRPORT_CODE) {
      if (values.includes(code)) {
        onChange([]);
      } else {
        onChange([code]);
      }
    } else {
      if (values.includes(code)) {
        onChange(values.filter((airportCode: string) => airportCode !== code));
      } else {
        onChange(values.concat(code));
      }
    }
  };

  const grouped = groupBy(options, (option: AirportResult) => option.group.name);
  const hasNoResults: boolean = !options.length;
  const hasNoGroupedResults: boolean = !Object.entries(grouped).length;

  const getLabel = (option: AirportResult): string | JSX.Element =>
    option.airport.code === NO_AIRPORT_CODE && noFlightsLabel ? noFlightsLabel : option.airport.name;

  return (
    <Container data-testid={TestId.dealFinder.airports.list} displayInColumns={!isNotGroupResults}>
      {!isNotGroupResults && (
        <div>
          {!hasNoGroupedResults && <GroupTitle>{t('common__label--regions')}</GroupTitle>}
          {!isNotGroupResults &&
            Object.entries(grouped)
              .sort(AirportSorter.compare)
              .map(([key, value]) => {
                const group: Airport = value[0].group;
                const groupAirport: AirportResult = { airport: group, group };
                return (
                  <GroupOption
                    key={key}
                    group={groupAirport}
                    airports={value}
                    selected={values}
                    available={available}
                    onChange={onChange}
                  />
                );
              })}
        </div>
      )}
      <div>
        {!hasNoResults && <AirportsTitle>{t('common__label--airports')}</AirportsTitle>}
        {options.sort(AirportSorter.unGrouped).map((option: AirportResult) => (
          <Option
            key={option.airport.name}
            id={option.airport.code}
            label={getLabel(option)}
            onClick={handleOnSelect}
            selected={values.includes(option.airport.code)}
            isDisabled={!!available.length && !available.includes(option.airport.code)}
          />
        ))}
        {hasNoResults && <NoResults>{t('search__common--no-results')}</NoResults>}
      </div>
    </Container>
  );
};
